import React, { useState } from 'react';
import CoreSkeletonBox from 'src/shared/CoreSkeletonBox';
import styled from 'styled-components';
import {
	firstSectionWrapperClass,
	updatedTextClass,
	updatedTextWrapperClass,
} from '@components/report/style.css';
import { useNbReportGet } from 'src/apis/orval/endpoints/v2-report/v2-report';
import { useSearchParams } from 'react-router-dom';
import { useReportPollingContext } from '@components/report/report-layout/ReportPollingContext';
import { CoreButton } from '@featuring-corp/components';
import ReportSection from '@components/report/report-section/ReportSection';
import ReportIndex from '@components/report/report-index/ReportIndex';
import useScrollIntoView from '@components/report/useScrollntoView';
import SectionMessages from '@components/report/section-messages/SectionMessages';
import { KeyMetricsNbAdapter } from '@components/report/section-content/nb/key-metrics/KeyMetricsNbAdapter';
import { VisitorsAndViewsAdapter } from '@components/report/section-content/nb/visitors-and-views/VisitorsAndViewsAdapter';
import { PostsAdapter } from '@components/report/section-content/nb/posts/PostsAdapter';
import { SimilarGroupNbAdapter } from '@components/report/section-content/nb/similar-group/SimilarGroupNbAdapter';
import KeyMetricsNb from '@components/report/section-content/nb/key-metrics/KeyMetrics';
import VisitorsAndViews from '@components/report/section-content/nb/visitors-and-views/VisitorsAndViews';
import Posts from '@components/report/section-content/nb/posts/Posts';
import SimilarGroupNb, {
	SimilarGroupNbProps,
} from '@components/report/section-content/nb/similar-group/SimilarGroupNb';

const indexItems = ['핵심 지표', '방문자 및 조회 수', '게시글', '유사 그룹'];

const NaverBlogContainer = () => {
	const [search_params] = useSearchParams();
	const platformPk = search_params.get('pk') as string;
	const {
		isReportLoading,
		isUpdateLoading,
		needUpdate,
		readyForUpdate,
		isPollingFailed,
	} = useReportPollingContext();

	const { selectedSection, moveScroll } = useScrollIntoView('h2');

	const {
		data: { keyMetrics, visitorsAndViews, posts, similarGroup } = {},
		isLoading: isQueryLoading,
		isError: isQueryError,
		error,
		refetch,
	} = useNbReportGet(platformPk, {
		query: {
			select: (data) => ({
				keyMetrics: new KeyMetricsNbAdapter(data).adapt(),
				visitorsAndViews: new VisitorsAndViewsAdapter(data).adapt(),
				posts: new PostsAdapter(data).adapt(),
				similarGroup: new SimilarGroupNbAdapter(data).adapt(),
			}),
			refetchOnWindowFocus: false,
			enabled: !!platformPk,
			retry: false,
		},
	});

	const isLoading = isQueryLoading || isReportLoading;
	// polling 이 완료 없이 종료된 경우(에러/시간 초과)도 에러 화면과 동일하게 처리
	const isError = isQueryError || isPollingFailed;

	return (
		<>
			<ReportIndex
				indexItems={indexItems}
				selectedSection={selectedSection ?? ''}
				handleMoveScroll={moveScroll}
			/>
			<ReportContentWrapper>
				{/* 섹션 메시지 */}
				<SectionMessages
					isError={isError}
					errorCode={(error as any)?.statusCode}
					isLoading={isLoading}
				/>

				<div className={firstSectionWrapperClass}>
					<div className={updatedTextWrapperClass}>
						<p className={updatedTextClass}>
							Last update:{' '}
							{isLoading ? (
								<CoreSkeletonBox _width="80px" _height="14px" radius="4px" />
							) : (
								keyMetrics?.updatedAt ?? ' - '
							)}
						</p>
						{needUpdate && (
							<CoreButton
								text={'업데이트'}
								buttonType="secondary"
								size="sm"
								isLoading={isUpdateLoading}
								disabled={!isUpdateLoading && !readyForUpdate}
								onClick={() => refetch()}
							/>
						)}
					</div>

					{/* 핵심 지표 */}
					<ReportSection
						title="핵심 지표"
						tooltip="인플루언서의 효율성과 영향력을 정량적으로 측정하는 핵심 지표입니다. 지표에 대한 더 자세한 분석은 각각의 섹션에서 확인할 수 있습니다. "
						description="인플루언서 분석을 위한 주요 지표들을 한 눈에 확인할 수 있습니다. 제시된 평균값은 최근 수집된 10개 콘텐츠 기준으로 산출되었습니다."
					>
						<KeyMetricsNb
							isLoading={isLoading}
							isError={isError}
							{...keyMetrics?.props}
						/>
					</ReportSection>
				</div>

				<ReportSection
					title="방문자 및 조회 수"
					description="계정의 팔로워 수와 질을 심층적으로 이해하고, 영향력을 측정합니다. 또한 장기적 관점에서의 성장 잠재력과 시장 내 위치를 평가할 수 있습니다."
				>
					<VisitorsAndViews
						isLoading={isLoading}
						isError={isError}
						{...visitorsAndViews}
					/>
				</ReportSection>

				<ReportSection title="게시글">
					<Posts isLoading={isLoading} isError={isError} {...posts} />
				</ReportSection>

				<ReportSection
					title="유사 그룹"
					tooltip="카테고리와 누적 방문자 수 등이 유사한 계정들로 구성되어 있습니다. 관련성이 높은 다른 인플루언서를 발견하고, 비교 분석을 통해 해당 계정의 유사 그룹 내 퍼포먼스를 파악해 보세요."
					description={
						<>
							<span>{similarGroup?.desc?.userName ?? '-'}</span>과(와) 유사한 계정을
							보여줍니다. <span>{similarGroup?.desc?.category ?? '-'}</span>에서{' '}
							<span>{similarGroup?.desc?.followers ?? '-'} 누적 방문자 수</span>를 가진
							인플루언서들입니다.
						</>
					}
				>
					<SimilarGroupNb
						{...(similarGroup?.props as SimilarGroupNbProps<string[]>)}
						isLoading={isLoading}
						isError={isError}
					/>
				</ReportSection>
			</ReportContentWrapper>
		</>
	);
};

const ReportContentWrapper = styled.div`
	overflow: hidden;
	height: auto;
	min-height: 100%;
	flex-grow: 1;
	flex-shrink: 0;
	padding: 60px 0 150px;

	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;

	gap: 80px;
`;

export default NaverBlogContainer;
